export type AuthStrategy = "email_code" | "email_link" | "password";

export type ClerkErrorEntry = {
	code?: string;
	message?: string;
	longMessage?: string;
};

export type ClerkAuthError = Error & {
	isSignUp?: boolean;
	errors?: ClerkErrorEntry[];
};

type ClerkError = {
	errors: ClerkErrorEntry[];
	message?: string;
};

const isClerkError = (error: unknown): error is ClerkError => {
	return (
		typeof error === "object" &&
		error !== null &&
		"errors" in error &&
		Array.isArray((error as ClerkError).errors)
	);
};

const getPrimaryClerkError = (error: unknown): ClerkErrorEntry | undefined => {
	return isClerkError(error) ? error.errors[0] : undefined;
};

export const isDuplicateUserError = (error: unknown): boolean => {
	return (
		isClerkError(error) && error.errors[0]?.code === "form_identifier_exists"
	);
};

export const isTestModeEmail = (email: string) => {
	return (
		/^demo\+.+@memorang\.com$/.test(email) && !email.includes("clerk_test")
	);
};

export const isIncorrectPasscodeError = (error: unknown): boolean => {
	return isClerkError(error) && error.errors[0]?.code === "form_code_incorrect";
};

export const isIncorrectPasswordError = (error: unknown): boolean => {
	return (
		isClerkError(error) && error.errors[0]?.code === "form_password_incorrect"
	);
};

export const formatError = (error: unknown): string => {
	const clerkError = getPrimaryClerkError(error);
	if (clerkError) {
		return clerkError.longMessage || clerkError.message || "Unknown error";
	}

	if (error instanceof Error && error.message) {
		return error.message;
	}

	if (typeof error === "string" && error.length > 0) {
		return error;
	}

	return "Unknown error";
};

export const isSignUpError = (error: unknown): boolean => {
	return typeof error === "object" && error !== null && "isSignUp" in error;
};

export const isAuthenticationInvalidError = (error: unknown): boolean => {
	return (
		isClerkError(error) && error.errors[0]?.code === "authentication_invalid"
	);
};

export const getAuthStrategy = (
	email: string,
	passcode: string | undefined,
): AuthStrategy => {
	if (passcode && isTestModeEmail(email)) {
		return "password";
	}
	if (passcode) {
		return "email_code";
	}
	return "email_link";
};

export const tagAsSignUpError = (error: unknown): ClerkAuthError => {
	const base = error instanceof Error ? error : new Error("Sign-up failed");
	const errors = isClerkError(error) ? error.errors : undefined;
	return Object.assign(base, { isSignUp: true, ...(errors && { errors }) });
};

export const retryDelay = (): Promise<void> =>
	new Promise((resolve) => setTimeout(resolve, 500));

const NON_ACTIONABLE_AUTH_MESSAGE_PATTERNS = [
	"signed out",
	"authentication invalid",
	"no sign up/sign in was found with id",
] as const;

export const isNonActionableAuthError = (error: unknown): boolean => {
	if (isAuthenticationInvalidError(error)) {
		return true;
	}

	const message = formatError(error).toLowerCase();
	return NON_ACTIONABLE_AUTH_MESSAGE_PATTERNS.some((pattern) =>
		message.includes(pattern),
	);
};
